import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useSession } from '../auth/SessionContext';
import { VerifyEmailBanner } from '../components/VerifyEmailBanner';
import { useSupportBadge } from '../lib/badges';
import { homeFor, sectionsFor } from '../nav';
import { AppShell, PublicShell } from '../ui';

/**
 * Which shell a page is wearing.
 *
 * There are two, and the session decides: signed out you get a mark and two
 * buttons, signed in you get the sidebar. The pages themselves know nothing
 * about either, so adding a page is still adding a route.
 *
 * The public addresses are listed rather than inferred: `/signup`,
 * `/verify-email` and `/reset-password` are reachable WITH a session (an
 * operator confirming a colleague's link, somebody resetting a password they
 * still remember) and they are not application pages, so they keep the plain
 * chrome either way. `/terms` and `/privacy` are documents anybody may read,
 * signed in or not. `/` and `/login` redirect away for a signed-in visitor
 * before this ever matters.
 */
const PUBLIC_PATHS = new Set([
  '/',
  '/login',
  '/signup',
  '/verify-email',
  '/forgot-password',
  '/reset-password',
  '/terms',
  '/privacy',
]);

/**
 * The addresses that get a NARROW column.
 *
 * Every one of them is a single form, and a form is as wide as its longest
 * input: the content column fills the screen everywhere else in this app, and
 * an email field stretched across a wide monitor would be the one place that
 * made a page harder to use rather than easier. `/` is deliberately NOT here —
 * the front door is a landing page and its sections want the width. The two
 * documents are not here either: they cap their own line length with the
 * reading measure and want the room for it.
 */
const NARROW_PATHS = new Set([
  '/login',
  '/signup',
  '/verify-email',
  '/forgot-password',
  '/reset-password',
]);

export function Layout(): React.JSX.Element {
  const { status, session, isAdmin, signOut } = useSession();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const isPublicPage = PUBLIC_PATHS.has(pathname);
  const signedIn = status === 'authenticated' && session !== null && !isPublicPage;
  /* Asked only once there is a session: a visitor has no tickets to count. */
  const supportUnread = useSupportBadge(isAdmin, signedIn ? pathname : '');

  if (signedIn) {
    return (
      <AppShell
        sections={sectionsFor(isAdmin)}
        homePath={homeFor(isAdmin)}
        badges={{ [isAdmin ? '/admin/support' : '/support']: supportUnread }}
        user={{
          name: session.creator?.displayName ?? '',
          email: session.user.email,
          role: isAdmin ? 'Operator' : 'Creator',
        }}
        onSignOut={() => {
          // `.finally()`, not `.then()`: signing out promises one outcome —
          // landing on `/login` — and that has to hold even if `signOut`
          // itself ever starts rejecting again (it does not today, but
          // nothing here should depend on that staying true).
          void signOut().finally(() => navigate('/login'));
        }}
      >
        {/* Above every page, for a creator whose address is not confirmed:
            the one thing standing between them and a submission. */}
        <VerifyEmailBanner />
        <Outlet />
      </AppShell>
    );
  }

  /* Still asking who this is, on an address that will need a session: show the
     frame without the sign-in buttons rather than flashing them at somebody who
     turns out to be signed in. */
  return (
    <PublicShell actions={status !== 'loading' || isPublicPage} narrow={NARROW_PATHS.has(pathname)}>
      <Outlet />
    </PublicShell>
  );
}
